import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ROUTES } from '../src/constants/routes';

interface RouteInfo {
  path: string;
  description: string;
  purpose: string;
  apiEndpoints: string[];
  parameters?: string[];
  authRequired?: boolean;
  userType?: 'client' | 'healer' | 'public';
}

const DevPage: React.FC = () => {
  const [filter, setFilter] = useState<'all' | 'client' | 'healer' | 'public'>('all');
  const [showOnlyWithAuth, setShowOnlyWithAuth] = useState(false);

  const routes: RouteInfo[] = [
    // Homepage
    {
      path: ROUTES.HOME,
      description: 'Homepage / Landing Page',
      purpose: 'Main landing page for therapists and potential clients',
      apiEndpoints: [],
      userType: 'public'
    },

    // Authentication
    {
      path: ROUTES.AUTH.CALLBACK,
      description: 'OAuth Callback Handler',
      purpose: 'Handles OAuth authentication callbacks from providers',
      apiEndpoints: [],
      parameters: ['code', 'state', 'error', 'provider'],
      userType: 'public'
    },

    // Client Onboarding Flow
    {
      path: ROUTES.GET_STARTED.INTAKE,
      description: 'Intake Email Entry',
      purpose: 'Capture client email and create/get client record',
      apiEndpoints: [
        'POST /supabase/functions/v1/clients-create-or-get',
        'POST /supabase/functions/v1/auth-signin (magic link)'
      ],
      parameters: ['ref', 'promo', 'healer'],
      userType: 'public'
    },
    {
      path: ROUTES.GET_STARTED.VERIFY,
      description: 'Email Verification',
      purpose: 'Verify client email through magic link or token',
      apiEndpoints: [
        'GET /supabase/functions/v1/auth-session (verify token)'
      ],
      parameters: ['email', 'token', 'resent'],
      userType: 'public'
    },
    {
      path: ROUTES.GET_STARTED.ASSESSMENT,
      description: 'Multi-Step Intake Form',
      purpose: 'Comprehensive client assessment and intake data collection',
      apiEndpoints: [
        'POST /supabase/functions/v1/clients-save-intake-data (auto-save)',
        'POST /supabase/functions/v1/clients-submit-intake (final submission)'
      ],
      parameters: ['step', 'section', 'saved', 'return'],
      authRequired: true,
      userType: 'client'
    },
    {
      path: ROUTES.GET_STARTED.COMPLETE,
      description: 'Intake Completion Success',
      purpose: 'Show intake completion status with next steps',
      apiEndpoints: [],
      parameters: ['intakeId', 'nextStep', 'healerAssigned'],
      authRequired: true,
      userType: 'client'
    },

    // Client Portal
    {
      path: ROUTES.PORTAL.LOGIN,
      description: 'Client Login Page',
      purpose: 'Client authentication with magic link or password',
      apiEndpoints: [
        'POST /supabase/functions/v1/auth-signin'
      ],
      parameters: ['redirect', 'expired', 'verified'],
      userType: 'public'
    },
    {
      path: ROUTES.PORTAL.DASHBOARD,
      description: 'Client Dashboard',
      purpose: 'Main client portal with overview and quick actions',
      apiEndpoints: [
        'GET /supabase/functions/v1/client-me',
        'GET /supabase/functions/v1/client-bookings'
      ],
      parameters: ['welcome', 'notification'],
      authRequired: true,
      userType: 'client'
    },
    {
      path: ROUTES.PORTAL.SESSIONS,
      description: 'Client Sessions Management',
      purpose: 'View and manage upcoming/past therapy sessions',
      apiEndpoints: [
        'GET /supabase/functions/v1/client-bookings'
      ],
      parameters: ['view', 'filter', 'healer', 'date', 'status'],
      authRequired: true,
      userType: 'client'
    },
    {
      path: ROUTES.PORTAL.RESOURCES,
      description: 'Client Resource Library',
      purpose: 'Access personalized content, exercises, and materials',
      apiEndpoints: [
        'GET /supabase/functions/v1/client-content-library',
        'GET /supabase/functions/v1/content-catalog'
      ],
      parameters: ['category', 'access', 'search', 'sort', 'page'],
      authRequired: true,
      userType: 'client'
    },
    {
      path: ROUTES.PORTAL.BILLING,
      description: 'Client Billing & Payments',
      purpose: 'View payment history, methods, and subscriptions',
      apiEndpoints: [
        'GET /supabase/functions/v1/client-payments',
        'GET /supabase/functions/v1/client-subscriptions'
      ],
      parameters: ['view', 'invoice', 'year', 'status'],
      authRequired: true,
      userType: 'client'
    },

    // Healer Portal (Admin) - 5-Tab Interface
    {
      path: ROUTES.HEALER.DASHBOARD,
      description: 'Admin Portal Dashboard (5-Tab Interface)',
      purpose: 'Comprehensive admin interface: Client Management, Content Management, Appointments, Appointment Types, Software Waitlist',
      apiEndpoints: [
        'GET /supabase/functions/v1/admin-clients',
        'GET /supabase/functions/v1/admin-bookings', 
        'GET /supabase/functions/v1/admin-content',
        'GET /supabase/functions/v1/appointment-types',
        'GET /supabase/functions/v1/admin-software-waitlist'
      ],
      parameters: ['tab=clients|content|appointments|types|waitlist', 'search', 'filter', 'page'],
      authRequired: true,
      userType: 'healer'
    },
    {
      path: ROUTES.HEALER.CLIENTS,
      description: 'Client Management Tab',
      purpose: 'Comprehensive client management with 11 mock clients, filtering, and detailed views',
      apiEndpoints: [
        'GET /supabase/functions/v1/admin-clients',
        'GET /supabase/functions/v1/admin-client-details'
      ],
      parameters: ['status', 'filter', 'search', 'sort', 'tag', 'page'],
      authRequired: true,
      userType: 'healer'
    },
    {
      path: ROUTES.HEALER.CLIENT_DETAIL,
      description: 'Individual Client Detail View',
      purpose: 'Detailed client management with notes, sessions, documents, billing history',
      apiEndpoints: [
        'GET /supabase/functions/v1/admin-client-details/:id',
        'GET /supabase/functions/v1/admin-client-bookings/:id',
        'GET /supabase/functions/v1/admin-client-payments/:id',
        'POST /supabase/functions/v1/admin-client-notes',
        'PUT /supabase/functions/v1/admin-client-update'
      ],
      parameters: ['tab', 'sessionId', 'action', 'return'],
      authRequired: true,
      userType: 'healer'
    },
    {
      path: ROUTES.HEALER.SCHEDULE,
      description: 'Appointments Management Tab',
      purpose: 'View and manage 6 mock bookings with scheduling capabilities',
      apiEndpoints: [
        'GET /supabase/functions/v1/availability',
        'POST /supabase/functions/v1/availability',
        'GET /supabase/functions/v1/admin-bookings',
        'POST /supabase/functions/v1/admin-book-session'
      ],
      parameters: ['view', 'date', 'week', 'month', 'type', 'location'],
      authRequired: true,
      userType: 'healer'
    },
    {
      path: ROUTES.HEALER.ANALYTICS,
      description: 'Practice Analytics & Reports',
      purpose: 'Revenue, utilization, retention, and growth analytics (placeholder)',
      apiEndpoints: [
        'GET /supabase/functions/v1/admin-analytics',
        'GET /supabase/functions/v1/admin-reports',
        'POST /supabase/functions/v1/admin-export-data'
      ],
      parameters: ['report', 'period', 'compare', 'breakdown', 'export'],
      authRequired: true,
      userType: 'healer'
    },
    {
      path: ROUTES.HEALER.CONTENT,
      description: 'Content Management Tab',
      purpose: 'Manage 9 content items with upload, edit, delete capabilities',
      apiEndpoints: [
        'GET /supabase/functions/v1/admin-content',
        'POST /supabase/functions/v1/admin-content',
        'PUT /supabase/functions/v1/admin-content/:id',
        'DELETE /supabase/functions/v1/admin-content/:id'
      ],
      parameters: ['tab', 'type', 'status', 'search'],
      authRequired: true,
      userType: 'healer'
    },
    {
      path: ROUTES.HEALER.SETTINGS,
      description: 'Appointment Types Management Tab',
      purpose: '7 appointment types configuration with pricing and duration settings',
      apiEndpoints: [
        'GET /supabase/functions/v1/appointment-types',
        'POST /supabase/functions/v1/appointment-types',
        'PUT /supabase/functions/v1/appointment-types/:id',
        'DELETE /supabase/functions/v1/appointment-types/:id'
      ],
      parameters: ['section', 'saved', 'wizard'],
      authRequired: true,
      userType: 'healer'
    },
    {
      path: '/admin/software-waitlist',
      description: 'Software Waitlist Management Tab',
      purpose: 'Manage 7 software waitlist entries across CycleChef, HTMA, and Practice Management tools',
      apiEndpoints: [
        'GET /supabase/functions/v1/admin-software-waitlist'
      ],
      parameters: ['software_type', 'interest_level', 'export'],
      authRequired: true,
      userType: 'healer'
    },

    // Public Booking
    {
      path: ROUTES.SCHEDULE.BOOK,
      description: 'Public Booking Page',
      purpose: 'Public appointment booking interface',
      apiEndpoints: [
        'GET /supabase/functions/v1/appointment-types',
        'GET /supabase/functions/v1/availability',
        'POST /supabase/functions/v1/public-book-session',
        'POST /supabase/functions/v1/create-checkout-session'
      ],
      parameters: ['type', 'date', 'healer', 'step'],
      userType: 'public'
    },
    {
      path: ROUTES.SCHEDULE.CONFIRMED,
      description: 'Booking Confirmation',
      purpose: 'Show successful booking confirmation with details',
      apiEndpoints: [
        'GET /supabase/functions/v1/verify-session'
      ],
      parameters: ['sessionId', 'paymentStatus', 'receipt'],
      userType: 'public'
    },

    // Public Resources
    {
      path: ROUTES.RESOURCES.CATALOG,
      description: 'Public Content Catalog',
      purpose: 'Browse and purchase wellness content, courses, and resources',
      apiEndpoints: [
        'GET /supabase/functions/v1/content-catalog',
        'POST /supabase/functions/v1/create-checkout-session'
      ],
      parameters: ['category', 'type', 'price', 'level', 'healer', 'search', 'sort', 'page'],
      userType: 'public'
    },
    {
      path: ROUTES.RESOURCES.COMMUNITY,
      description: 'Community & Events',
      purpose: 'Community discussions, events, and group activities',
      apiEndpoints: [
        'GET /community-posts',
        'GET /community-events'
      ],
      parameters: ['tab', 'topic', 'filter', 'type'],
      userType: 'public'
    },
    {
      path: ROUTES.RESOURCES.TOOLS,
      description: 'Recommended Apps & Tools',
      purpose: 'Curated list of wellness apps and digital tools',
      apiEndpoints: [
        'GET /recommended-tools',
        'POST /tool-ratings'
      ],
      parameters: ['platform', 'category', 'price', 'sort'],
      userType: 'public'
    },

    // Checkout
    {
      path: ROUTES.CHECKOUT.SUCCESS,
      description: 'Purchase Success Page',
      purpose: 'Confirmation page after successful purchase',
      apiEndpoints: [
        'GET /verify-purchase',
        'POST /send-receipt'
      ],
      parameters: ['orderId', 'type', 'itemId', 'receipt'],
      userType: 'public'
    },

    // Legal Pages
    {
      path: ROUTES.LEGAL.BASE + '/:page',
      description: 'Legal Documentation',
      purpose: 'Privacy policy, terms of service, HIPAA compliance',
      apiEndpoints: [],
      parameters: ['page (privacy|terms|hipaa)'],
      userType: 'public'
    },
  ];

  const filteredRoutes = routes.filter(route => {
    if (filter !== 'all' && route.userType !== filter) return false;
    if (showOnlyWithAuth && !route.authRequired) return false;
    return true;
  });

  const getRouteUrl = (path: string, parameters?: string[]) => {
    if (parameters && parameters.length > 0) {
      const sampleParams = parameters.slice(0, 2).map(param => {
        const [key, ...valueParts] = param.split('=');
        const value = valueParts.length > 0 ? valueParts.join('=') : 'sample';
        return `${key}=${value}`;
      }).join('&');
      return `${path}?${sampleParams}`;
    }
    return path;
  };

  const getUserTypeColor = (userType?: string) => {
    switch (userType) {
      case 'client': return 'bg-blue-100 text-blue-800';
      case 'healer': return 'bg-green-100 text-green-800';
      case 'public': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">🛠️ Development Route Explorer</h1>
          <p className="text-gray-600 mb-4">
            Complete overview of all application routes with current Supabase Edge Functions and MCP orchestration system.
          </p>
          <div className="bg-green-100 border border-green-300 text-green-800 px-4 py-3 rounded mb-6">
            <strong>🎉 Recently Updated:</strong> Admin portal now features 5 working tabs, all Edge Functions deployed, dev mode authentication active.
          </div>
          
          {/* Filters */}
          <div className="flex flex-wrap gap-4 mb-6 p-4 bg-white rounded-lg shadow">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">Filter by User Type:</label>
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value as any)}
                className="border border-gray-300 rounded px-3 py-1 text-sm"
              >
                <option value="all">All Routes</option>
                <option value="public">Public</option>
                <option value="client">Client</option>
                <option value="healer">Healer</option>
              </select>
            </div>
            
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={showOnlyWithAuth}
                onChange={(e) => setShowOnlyWithAuth(e.target.checked)}
                className="rounded"
              />
              Only show routes requiring authentication
            </label>
          </div>

          {/* Route Statistics */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            <div className="bg-white p-4 rounded-lg shadow text-center">
              <div className="text-2xl font-bold text-blue-600">{routes.filter(r => r.userType === 'public').length}</div>
              <div className="text-sm text-gray-600">Public Routes</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow text-center">
              <div className="text-2xl font-bold text-green-600">{routes.filter(r => r.userType === 'client').length}</div>
              <div className="text-sm text-gray-600">Client Routes</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow text-center">
              <div className="text-2xl font-bold text-purple-600">{routes.filter(r => r.userType === 'healer').length}</div>
              <div className="text-sm text-gray-600">Healer Routes</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow text-center">
              <div className="text-2xl font-bold text-red-600">{routes.filter(r => r.authRequired).length}</div>
              <div className="text-sm text-gray-600">Auth Required</div>
            </div>
          </div>
        </div>

        {/* Routes Grid */}
        <div className="grid gap-6">
          {filteredRoutes.map((route, index) => (
            <div key={index} className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">{route.description}</h3>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getUserTypeColor(route.userType)}`}>
                      {route.userType}
                    </span>
                    {route.authRequired && (
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        Auth Required
                      </span>
                    )}
                  </div>
                  <p className="text-gray-600 mb-3">{route.purpose}</p>
                  <div className="font-mono text-sm text-blue-600 bg-blue-50 px-3 py-2 rounded mb-3">
                    {route.path}
                  </div>
                </div>
                
                <div className="flex gap-2">
                  <Link
                    to={getRouteUrl(route.path, route.parameters)}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm font-medium"
                  >
                    Visit Route
                  </Link>
                  <Link
                    to={route.path}
                    className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 text-sm font-medium"
                  >
                    Clean URL
                  </Link>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                {/* API Endpoints */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">🔗 API Endpoints:</h4>
                  {route.apiEndpoints.length > 0 ? (
                    <ul className="space-y-1">
                      {route.apiEndpoints.map((endpoint, i) => (
                        <li key={i} className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">
                          {endpoint}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-gray-500 text-sm italic">No API calls</p>
                  )}
                </div>

                {/* URL Parameters */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">⚙️ URL Parameters:</h4>
                  {route.parameters && route.parameters.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {route.parameters.map((param, i) => (
                        <span key={i} className="inline-block bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded">
                          {param}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm italic">No parameters</p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {filteredRoutes.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">No routes match your current filters.</p>
          </div>
        )}

        {/* Development Tools */}
        <div className="mt-12 bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">🔧 Development Tools</h2>
          <div className="grid md:grid-cols-3 gap-4 mb-6">
            <Link
              to="http://localhost:54324"
              target="_blank"
              className="block p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
            >
              <h3 className="font-medium text-gray-900">📧 Supabase Inbucket</h3>
              <p className="text-sm text-gray-600">View magic link emails</p>
            </Link>
            <Link
              to="http://localhost:54323"
              target="_blank"
              className="block p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
            >
              <h3 className="font-medium text-gray-900">🗄️ Database Admin</h3>
              <p className="text-sm text-gray-600">Supabase Studio</p>
            </Link>
            <Link
              to={ROUTES.HEALER.DASHBOARD}
              className="block p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
            >
              <h3 className="font-medium text-gray-900">🧑‍⚕️ Quick Admin Portal</h3>
              <p className="text-sm text-gray-600">5-tab interface with dev mode</p>
            </Link>
          </div>

          {/* MCP Orchestration System */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">🤖 MCP Orchestration System</h3>
            <div className="bg-blue-50 p-4 rounded-lg mb-4">
              <p className="text-sm text-blue-800 mb-2">
                This project uses the claude-workflow MCP orchestration system for task management and agent coordination.
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium text-blue-900 mb-1">🎯 Core Features:</h4>
                  <ul className="text-xs text-blue-700 space-y-1">
                    <li>• Task creation and claiming system</li>
                    <li>• Bundle management for related tasks</li>
                    <li>• Agent performance tracking</li>
                    <li>• Token usage monitoring</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-medium text-blue-900 mb-1">📋 Recent Completions:</h4>
                  <ul className="text-xs text-blue-700 space-y-1">
                    <li>• ✅ Admin portal 5-tab interface</li>
                    <li>• ✅ admin-software-waitlist function</li>
                    <li>• ✅ All Edge Functions deployed</li>
                    <li>• ✅ Dev mode authentication</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Current Architecture Status */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">🏗️ Current Architecture</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-green-50 p-4 rounded-lg">
                <h4 className="font-medium text-green-800 mb-2">✅ Completed</h4>
                <ul className="text-sm text-green-700 space-y-1">
                  <li>• Supabase Edge Functions migration</li>
                  <li>• 5-tab admin portal interface</li>
                  <li>• Dev mode authentication bypass</li>
                  <li>• MCP orchestration integration</li>
                  <li>• TypeScript type checking</li>
                </ul>
              </div>
              <div className="bg-yellow-50 p-4 rounded-lg">
                <h4 className="font-medium text-yellow-800 mb-2">⚠️ Known Issues</h4>
                <ul className="text-sm text-yellow-700 space-y-1">
                  <li>• CORS issues on 2/5 admin tabs</li>
                  <li>• TypeScript errors in archived files</li>
                  <li>• Some test mocks need updating</li>
                  <li>• Legacy API endpoint references</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DevPage;